import React, { useMemo } from "react";
import { useForm, ArrayField } from "react-hook-form";
import { useIntl } from "react-intl";
import { get } from "lodash";
import useGetSkills from "../../graphql/hooks/queries/useGetSkills";
import useTranslations from "../../hooks/useTranslations";
import { IconProps } from "../icons";
import { Input, Textarea } from "../wombat-ui";
import AutoComplete from "../wombat-ui/components/Form/Input/AutoComplete";

import { TemplateQuestionFormInput } from "./TemplateQuestionForm";

type TemplateQuestionFormHeaderProps = {
  register: ReturnType<typeof useForm>["register"];
  unregister: ReturnType<typeof useForm>["unregister"];
  setValue: ReturnType<typeof useForm>["setValue"];
  getValues: ReturnType<typeof useForm>["getValues"];
  errors: ReturnType<typeof useForm>["errors"];
  error?: boolean;
  icon: React.ReactElement<IconProps>;
  defaultValue: Partial<ArrayField<TemplateQuestionFormInput, "id">>;
  name: string;
};

const SKILL_ATTRIBUTES = ["id", "name", "kind", "locale", "__typename"];

const TemplateQuestionFormHeader: React.FC<TemplateQuestionFormHeaderProps> = ({
  register,
  icon,
  defaultValue,
  errors,
  unregister,
  setValue,
  getValues,
  name,
}) => {
  const t = useTranslations();
  const { locale } = useIntl();

  const values = getValues();

  return (
    <div>
      <div className="grid w-full grid-cols-12 gap-x-4 gap-y-2">
        <span className="flex items-center justify-center col-span-1">
          {icon}
        </span>
        <div className="items-center w-full col-span-10">
          {defaultValue.type === "TemplateQuestionSkill" ? (
            <>
              <AutoComplete
                id={`${name}.subjectId`}
                srOnlyLabel
                listStrict
                defaultValue={defaultValue.subject}
                query={{
                  hook: useGetSkills,
                  accessKey: "skills",
                  hookOptions: { locale },
                }}
                onChange={(i) => {
                  setValue(`${name}.label`, i.name);
                  setValue(`${name}.subjectId`, i.id);
                  SKILL_ATTRIBUTES.forEach((attribute) =>
                    setValue(`${name}.subject.${attribute}`, i[attribute])
                  );
                }}
                getValue={(i) => i.id}
                getLabel={(i) => i.name}
                renderItem={(i) => i.name}
                emptyResult={() => t("mads.unknownSkill")}
                label={t("mads.pickSkill") as string}
                placeholder={t("mads.pickSkill") as string}
              />
              <input
                type="hidden"
                ref={register()}
                defaultValue={"Skill"}
                name={`${name}.subjectType`}
              />
              <input
                type="hidden"
                ref={register({ required: true })}
                defaultValue={defaultValue.subjectId || ""}
                name={`${name}.subjectId`}
              />
              <input
                type="hidden"
                ref={register()}
                defaultValue={
                  defaultValue.subject?.name ||
                  get(values, `${name}.label`) ||
                  ""
                }
                name={`${name}.label`}
              />
              {SKILL_ATTRIBUTES.map((attribute) => (
                <input
                  key={attribute}
                  type="hidden"
                  ref={register()}
                  defaultValue={defaultValue.subject?.[attribute] || ""}
                  name={`${name}.subject.${attribute}`}
                />
              ))}
            </>
          ) : (
            <Input
              id={`${name}.label`}
              srOnlyLabel
              defaultValue={defaultValue.label}
              name={`${name}.label`}
              ref={register({
                required: t("templates.titleRequired") as string,
              })}
              label={t("mads.writeQuestionHere") as string}
              error={errors?.label}
              placeholder={t("mads.writeQuestionHere") as string}
            />
          )}
        </div>
      </div>
      <div className="grid w-full grid-cols-12">
        <div className="col-span-10 col-start-2">
          <Textarea
            id={`${name}.description`}
            srOnlyLabel
            defaultValue={defaultValue.description}
            name={`${name}.description`}
            rows={2}
            ref={register()}
            label={t("templates.questionDescriptionPlaceholder") as string}
            placeholder={
              t("templates.questionDescriptionPlaceholder") as string
            }
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(TemplateQuestionFormHeader);
